import { Box, Button } from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';

import { FormikInput, FormikSelect } from 'components/Formik';
import LogoLoader from 'components/Loader/LogoLoader';
import Title from 'components/Title';
import { mileageExemptionOptions } from 'containers/CompanySettings/shared/options';
import { MILES_IN_KILOMETERS } from 'global/appConstants';
import { routePaths } from 'global/routePaths';
import { useFetchSettings } from 'hooks/companySettings/useFetchSettings';
import {
  useSaveCommuterMileageSetting,
  ISaveCommuterMileageSetting,
} from 'hooks/companySettings/useSaveCommuterMileage';
import { useFeatureList } from 'store/useFeatureStore';
import { useCompanyInfo } from 'store/useUserStore';

import { initialCommuterMileageData, commuterMileageValidation } from './schema';

const CommuterMileage: React.FC<unknown> = () => {
  const { t } = useTranslation(['common', 'companySettings']);
  const companyInfo = useCompanyInfo();
  const featureList = useFeatureList();

  const [formData, setFormData] = useState<any>(null);

  const { data, status, refetch } = useFetchSettings('commuter_mileage');
  const { status: saveNotificationStatus, mutateAsync } = useSaveCommuterMileageSetting();

  useEffect(
    () => {
      if (status === 'success') {
        const pFormData: any = {
          daily_mileage_cap:
            companyInfo?.milesInKm && data?.payload?.daily_mileage_cap
              ? (data?.payload?.daily_mileage_cap * MILES_IN_KILOMETERS).toFixed(2)
              : data?.payload?.daily_mileage_cap || '',
          mileage_exemption: data?.payload?.mileage_exemption
            ? mileageExemptionOptions.find((item: any) => item.value === data?.payload?.mileage_exemption)
            : mileageExemptionOptions[0],
        };
        setFormData(pFormData);
      }
    },
    // eslint-disable-next-line
    [status, companyInfo?.milesInKm]
  );

  const handleFormSubmit = async (values: typeof initialCommuterMileageData) => {
    const postData: ISaveCommuterMileageSetting = {
      daily_mileage_cap: companyInfo?.milesInKm
        ? (+values.daily_mileage_cap / MILES_IN_KILOMETERS).toString()
        : values.daily_mileage_cap,
      mileage_exemption: values.mileage_exemption.value,
    };

    mutateAsync(postData).then(() => {
      refetch();
    });
  };

  if (status === 'loading' || !formData) return <LogoLoader />;

  if (!featureList?.commuter_mileage) return <Navigate to={routePaths.companySettings.general} replace={true} />;

  return (
    <Box>
      <Box mb={{ base: '2', md: '0' }}>
        <Title hideBackButton> {t('companySettings:commuteMileage')}</Title>
      </Box>
      <Box mt="26px">
        <Formik
          initialValues={formData}
          validationSchema={commuterMileageValidation}
          enableReinitialize
          onSubmit={handleFormSubmit}
        >
          {() => (
            <Form noValidate>
              <Box w="390px">
                <FormikInput
                  type="text"
                  name="daily_mileage_cap"
                  label={
                    <>
                      {t('companySettings:dailyMileageCap')} (
                      {companyInfo.milesInKm ? t('common:kms') : t('common:miles')})
                    </>
                  }
                  inputProps={{
                    placeholder: 'eg. 0.665',
                  }}
                />

                <FormikSelect
                  name="mileage_exemption"
                  label={t('companySettings:dailyCommuteMileageExemption')}
                  options={mileageExemptionOptions}
                  selectProps={{
                    placeholder: t('companySettings:selectMileageExemption'),
                    isClearable: false,
                  }}
                />
              </Box>

              <Button
                mt="15"
                type="submit"
                colorScheme="secondary"
                isLoading={saveNotificationStatus === 'loading'}
                minWidth="100px"
              >
                {t('common:save')}
              </Button>
            </Form>
          )}
        </Formik>
      </Box>
    </Box>
  );
};
export default CommuterMileage;
